export type DocumentStatus =
	| 'NOT_UPLOADED'
	| 'UPLOADED_NOT_VERIFIED'
	| 'UPLOADED_REJECTED'
	| 'UPLOADED_APPROVED'
	| 'MORE_DOCUMENTS_REQUESTED';

export enum KycDocumentStatus {
	NOT_UPLOADED = 'NOT_UPLOADED',
	NOT_REQUIRED = 'NOT_REQUIRED',
	UPLOADED_NOT_VERIFIED = 'UPLOADED_NOT_VERIFIED',
	UPLOADED_REJECTED = 'UPLOADED_REJECTED',
	UPLOADED_APPROVED = 'UPLOADED_APPROVED',
	MORE_DOCUMENTS_REQUESTED = 'MORE_DOCUMENTS_REQUESTED',
}

export enum KycResetScope {
	PROOF = 'PROOF',
	VEHICLE = 'VEHICLE',
	FORM = 'FORM',
	DECLARATION = 'DECLARATION',
}

// Display-only KYC review-status sentinels, used when a requirement has no real
// AcceptanceStatus yet. Shared so the backend (e.g. Excel exports) and the
// frontend KYC views read the same values instead of redefining them.
export const EXPECTED_STATUS = 'EXPECTED' as const;
export const NOT_REQUIRED_STATUS = 'NOT_REQUIRED' as const;

// IMPORTANT: keep these helpers generic over the status type — do NOT retype
// them against AcceptanceStatus directly. There are two distinct AcceptanceStatus
// declarations: the backend's (prisma, a string-literal union) and the
// frontend's (the generated OpenAPI nominal enum). They are NOT assignable to
// each other in return position, and the project's no-`as`-cast rule means we
// can't bridge them. Typing these helpers against either concrete AcceptanceStatus
// makes the other tier fail to compile on the returned value. Instead each caller
// passes its own status constants and gets its own type (S) back, so one copy of
// the rules can be reused by both the Excel export and the investments-list KYC
// column. The extra `statuses`/`pending` argument is the price of that reuse.

/** Defaults a KYC review status to PENDING when a reviewer hasn't acted yet. */
export const normalizeKycReviewStatus = <S extends string>(
	status: S | null | undefined,
	pending: S
): S => status ?? pending;

/**
 * Rolls a set of declaration statuses up to a single review status, matching the
 * investments-list KYC column: any rejected → rejected, else any unreviewed →
 * pending, else (all approved) → approved.
 */
export const getDeclarationsOverallStatus = <S extends string>(
	declarations: { status: S | null }[],
	statuses: { approved: S; pending: S; rejected: S }
): S => {
	const total = declarations.length;
	const accepted = declarations.filter((d) => d.status === statuses.approved).length;
	const rejected = declarations.filter((d) => d.status === statuses.rejected).length;
	const unaccepted = declarations.filter(
		(d) => normalizeKycReviewStatus(d.status, statuses.pending) === statuses.pending
	).length;

	if (rejected > 0) {
		return statuses.rejected;
	}
	if (unaccepted > 0) {
		return statuses.pending;
	}
	return accepted === total ? statuses.approved : statuses.pending;
};
